import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { useState } from "react";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgButton } from "@intelligems/ig-theme/IgButton";
import {
  Bold,
  Code,
  Eraser,
  Italic,
  Link,
  List,
  ListOrdered,
  Redo2,
  SquareCode,
  Underline,
  Undo2,
} from "lucide-react";

import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import { FontSizeSelect } from "@src/components/RichTextEditor/MenuBar/FontSizeSelect";
import { MenuBarColorPicker } from "@src/components/RichTextEditor/MenuBar/MenuBarColorPicker";
import { RichEditorState } from "@src/components/RichTextEditor/types";
import { Editor } from "@tiptap/react";

interface MenuBarProps {
  editor: Editor;
  state: RichEditorState;
  disabled: boolean;
  supportToolbar?: boolean;
}

export const MenuBar = ({
  editor,
  state,
  disabled,
  supportToolbar = false,
}: MenuBarProps) => {
  const theme = useTheme();
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const validLink = linkUrl !== null && /^(https?:\/\/|mailto:)/i.test(linkUrl);
  if (!editor) {
    return null;
  }

  return (
    <Stack
      direction={"row"}
      sx={{
        borderBottom: `1px solid ${theme.igBorders.core.default}`,
        padding: 1,
        alignItems: "center",
        flexWrap: "wrap",
        ...(supportToolbar && {
          backgroundColor: theme.palette.action.hover,
          "& button": { color: theme.palette.text.primary },
          "& button.Mui-disabled": { color: theme.palette.text.disabled },
          "& button.active": { backgroundColor: theme.palette.action.selected },
        }),
      }}
    >
      <IgTooltip title="Undo">
        <IgIconButton
          aria-label="Undo"
          size={"small"}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!state.undoEnabled || disabled}
        >
          <Undo2 size={16} />
        </IgIconButton>
      </IgTooltip>
      <IgTooltip title="Redo">
        <IgIconButton
          aria-label="Redo"
          size={"small"}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!state.redoEnabled || disabled}
        >
          <Redo2 size={16} />
        </IgIconButton>
      </IgTooltip>

      {!supportToolbar && (
        <>
          <FontSizeSelect
            title={"text size"}
            disabled={disabled}
            value={state.fontSize}
            onChange={(fontSize) =>
              editor.chain().focus().setFontSize(fontSize).run()
            }
          />
          <MenuBarColorPicker
            title={"text color"}
            value={state.color}
            disabled={disabled}
            onChange={(color) => editor.chain().focus().setColor(color).run()}
          />
        </>
      )}
      <IgTooltip title="Bold">
        <IgIconButton
          aria-label="Bold"
          disabled={disabled}
          size={"small"}
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={state.bold ? "active" : ""}
        >
          <Bold size={16} />
        </IgIconButton>
      </IgTooltip>
      <IgTooltip title="Italic">
        <IgIconButton
          aria-label="Italic"
          disabled={disabled}
          size={"small"}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={state.italic ? "active" : ""}
        >
          <Italic size={16} />
        </IgIconButton>
      </IgTooltip>
      <IgTooltip title="Underline">
        <IgIconButton
          aria-label="Underline"
          size={"small"}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={state.underline ? "active" : ""}
        >
          <Underline size={16} />
        </IgIconButton>
      </IgTooltip>
      {supportToolbar && (
        <>
          <IgTooltip title="Bulleted list">
            <IgIconButton
              aria-label="Bulleted list"
              disabled={disabled}
              size="small"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <List size={16} />
            </IgIconButton>
          </IgTooltip>
          <IgTooltip title="Numbered list">
            <IgIconButton
              aria-label="Numbered list"
              disabled={disabled}
              size="small"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <ListOrdered size={16} />
            </IgIconButton>
          </IgTooltip>
          <IgTooltip title="Inline code">
            <IgIconButton
              aria-label="Inline code"
              disabled={disabled}
              size="small"
              onClick={() => editor.chain().focus().toggleCode().run()}
            >
              <Code size={16} />
            </IgIconButton>
          </IgTooltip>
          <IgTooltip title="Code block">
            <IgIconButton
              aria-label="Code block"
              disabled={disabled}
              size="small"
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            >
              <SquareCode size={16} />
            </IgIconButton>
          </IgTooltip>
          <IgTooltip title="Add link">
            <IgIconButton
              aria-label="Add link"
              disabled={disabled}
              size="small"
              onClick={() => {
                setLinkUrl(
                  (editor.getAttributes("link").href as string) ?? "https://"
                );
              }}
            >
              <Link size={16} />
            </IgIconButton>
          </IgTooltip>
          <IgTooltip title="Clear formatting">
            <IgIconButton
              aria-label="Clear formatting"
              disabled={disabled}
              size="small"
              onClick={() =>
                editor.chain().focus().clearNodes().unsetAllMarks().run()
              }
            >
              <Eraser size={16} />
            </IgIconButton>
          </IgTooltip>
        </>
      )}
      <IgDialog
        showDialog={linkUrl !== null}
        handleClose={() => setLinkUrl(null)}
        title="Edit link"
        fullWidth
        maxWidth="xs"
        minWidth="0"
        hideActions
        rawContent={
          <Stack spacing={4}>
            <IgTextField
              autoFocus
              fullWidth
              label="Link URL"
              value={linkUrl ?? ""}
              onChange={(event) => setLinkUrl(event.target.value)}
              helperText="Use an http, https, or mailto URL."
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <IgButton onClick={() => setLinkUrl(null)}>Cancel</IgButton>
              <IgButton
                disabled={disabled}
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .extendMarkRange("link")
                    .unsetLink()
                    .run();
                  setLinkUrl(null);
                }}
              >
                Remove link
              </IgButton>
              <IgButton
                disabled={disabled || !validLink}
                onClick={() => {
                  if (linkUrl) {
                    editor
                      .chain()
                      .focus()
                      .extendMarkRange("link")
                      .setLink({ href: linkUrl })
                      .run();
                  }
                  setLinkUrl(null);
                }}
              >
                Apply
              </IgButton>
            </Stack>
          </Stack>
        }
      />
    </Stack>
  );
};
